"use client";

import { c as _c } from "react/compiler-runtime";
import { useMediaQuery } from "@/hooks/use-media-query";
import { hasScrolledToEnd, useExternalLinks } from "@/lib/privacy-policy";
import { Button } from "@/shared/ui/button/button";
import { Dialog } from "@/shared/ui/dialog/dialog";
import { GlassButton } from "@/shared/ui/glass-button/glass-button";
import { ScrollArea } from "@/shared/ui/scroll-area/scroll-area";
import { Alert, Loader, Text } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./privacy-notice-dialog.module.scss";
export type PrivacyNoticeDialogProps = Readonly<{
  opened: boolean;
  onClose: () => void;
  html: string | null;
  title?: string;
  loadingLabel?: string;
  /** Login flow only — Decline / Accept. Hidden when omitted. */
  onDecline?: () => void;
  onAccept?: () => void;
  accepting?: boolean;
  declineLabel?: string;
  acceptLabel?: string;
  acceptingLabel?: string;
  error?: string | null;
  footerNote?: string;
}>;
export function PrivacyNoticeDialog(t0) {
  const $ = _c(39);
  const {
    opened,
    onClose,
    html,
    title: t1,
    loadingLabel: t2,
    onDecline,
    onAccept,
    accepting: t3,
    declineLabel: t4,
    acceptLabel: t5,
    acceptingLabel: t6,
    error,
    footerNote
  } = t0;
  const title = t1 === undefined ? "Privacy Notice" : t1;
  const loadingLabel = t2 === undefined ? "Loading privacy policy..." : t2;
  const accepting = t3 === undefined ? false : t3;
  const declineLabel = t4 === undefined ? "Decline & Logout" : t4;
  const acceptLabel = t5 === undefined ? "I Accept" : t5;
  const acceptingLabel = t6 === undefined ? "Accepting..." : t6;
  const showActions = Boolean(onDecline && onAccept);
  const htmlRef = useExternalLinks();
  const viewportRef = useRef(null);
  const isWideBreakpoint = useMediaQuery("(min-width: 599px)");
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  let t7;
  if ($[0] !== html || $[1] !== opened) {
    t7 = {
      opened,
      html
    };
    $[0] = html;
    $[1] = opened;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const [scrollSession, setScrollSession] = useState(t7);
  if (scrollSession.opened !== opened || scrollSession.html !== html) {
    setScrollSession({
      opened,
      html
    });
    setHasReachedEnd(false);
  }
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const viewport = viewportRef.current;
      if (!viewport) {
        return;
      }
      if (hasScrolledToEnd(viewport)) {
        setHasReachedEnd(true);
      }
    };
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const syncScrollEnd = t8;
  let t10;
  let t9;
  if ($[4] !== html || $[5] !== opened || $[6] !== showActions) {
    t9 = () => {
      if (!opened || !html || !showActions) {
        return;
      }
      const frame = requestAnimationFrame(syncScrollEnd);
      return () => cancelAnimationFrame(frame);
    };
    t10 = [opened, html, showActions, syncScrollEnd];
    $[4] = html;
    $[5] = opened;
    $[6] = showActions;
    $[7] = t10;
    $[8] = t9;
  } else {
    t10 = $[7];
    t9 = $[8];
  }
  useEffect(t9, t10);
  const t11 = isWideBreakpoint ? "78dvw" : "97dvw";
  const t12 = !showActions;
  const t13 = !showActions;
  const t14 = !showActions;
  let t15;
  if ($[9] !== acceptLabel || $[10] !== accepting || $[11] !== acceptingLabel || $[12] !== declineLabel || $[13] !== error || $[14] !== footerNote || $[15] !== hasReachedEnd || $[16] !== html || $[17] !== onAccept || $[18] !== onDecline || $[19] !== showActions) {
    t15 = showActions ? <div className={styles.actions}>{error ? <Alert color="red" variant="light">{error}</Alert> : null}<div className={styles.actionButtons}><GlassButton size="md" fullWidth={true} onClick={onDecline} disabled={accepting}>{declineLabel}</GlassButton><Button variant="primary" size="md" fullWidth={true} onClick={onAccept} disabled={accepting || !html || !hasReachedEnd}>{accepting ? acceptingLabel : acceptLabel}</Button></div>{footerNote ? <p className={styles.footerNote}>{footerNote}</p> : null}</div> : undefined;
    $[9] = acceptLabel;
    $[10] = accepting;
    $[11] = acceptingLabel;
    $[12] = declineLabel;
    $[13] = error;
    $[14] = footerNote;
    $[15] = hasReachedEnd;
    $[16] = html;
    $[17] = onAccept;
    $[18] = onDecline;
    $[19] = showActions;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = showActions ? viewportRef : undefined;
  const t17 = showActions ? syncScrollEnd : undefined;
  let t18;
  if ($[21] !== html || $[22] !== htmlRef || $[23] !== loadingLabel) {
    t18 = html ? <div ref={htmlRef} className={styles.html} dangerouslySetInnerHTML={{
      __html: html
    }} /> : <div className={styles.loader}><Loader size={48} /><Text c="dimmed">{loadingLabel}</Text></div>;
    $[21] = html;
    $[22] = htmlRef;
    $[23] = loadingLabel;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t16 || $[26] !== t17 || $[27] !== t18) {
    t19 = <ScrollArea className={styles.scrollArea} type="always" viewportRef={t16} onScrollPositionChange={t17}>{t18}</ScrollArea>;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== onClose || $[30] !== opened || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t19 || $[37] !== title) {
    t20 = <Dialog opened={opened} onClose={onClose} title={title} size={t11} withCloseButton={t12} closeOnClickOutside={t13} closeOnEscape={t14} bodyClassName={styles.body} contentClassName={styles.content} mantineBodyClassName={styles.mantineBody} footer={t15}>{t19}</Dialog>;
    $[29] = onClose;
    $[30] = opened;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t19;
    $[37] = title;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  return t20;
}